import React, { useState, useEffect } from 'react';
import { BarCode, Delete, Refresh, FileText, CloseSmall, PreviewOpen, Edit } from '@icon-park/react';
import { useLabelList } from '../hooks/useLabelList';
import { getQRCodeBaseUrl, fetchLabelDetail } from '../../../api/labels';
import { BUSINESS_CONFIG } from '../../../config/business.config';
import './LabelList.css';

/**
 * LabelList - 我的创建标签列表组件
 * 展示用户创建的所有标签，支持预览、编辑、查看二维码、删除等操作
 */
export function LabelList({ onCreateLabel, onEditLabel }) {
  const {
    labels,
    isLoading,
    error,
    pagination,
    handleDelete,
    changePage,
    refresh,
  } = useLabelList();

  // 二维码弹窗状态
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [currentLabelId, setCurrentLabelId] = useState(null);
  const [currentLabelName, setCurrentLabelName] = useState('');
  const [qrcodeBaseUrl, setQrcodeBaseUrl] = useState(null); // 从后端获取的二维码基础URL
  
  // 组件加载时从后端获取二维码基础URL
  useEffect(() => {
    const fetchQRCodeBaseUrl = async () => {
      try {
        const result = await getQRCodeBaseUrl();
        const baseUrl = result?.data || result || BUSINESS_CONFIG.QRCODE.BASE_URL || window.location.origin;
        setQrcodeBaseUrl(baseUrl);
      } catch (error) {
        console.warn('获取二维码基础URL失败，使用默认值:', error);
        setQrcodeBaseUrl(BUSINESS_CONFIG.QRCODE.BASE_URL || window.location.origin);
      }
    };
    fetchQRCodeBaseUrl();
  }, []);

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return dateString;
    }
  };

  // 显示二维码弹窗
  const handleShowQRCode = (labelId, labelName) => {
    setCurrentLabelId(labelId);
    setCurrentLabelName(labelName || '未命名标签');
    setShowQRCodeModal(true);
  };

  // 关闭二维码弹窗
  const handleCloseQRCodeModal = () => {
    setShowQRCodeModal(false);
    setCurrentLabelId(null);
    setCurrentLabelName('');
  };

  // 处理删除
  const handleDeleteClick = async (labelId) => {
    const success = await handleDelete(labelId);
    if (success) {
      // 可以显示成功提示
    }
  };

  // 生成标签访问URL
  const getLabelUrl = (labelId) => {
    const base = qrcodeBaseUrl || BUSINESS_CONFIG.QRCODE.BASE_URL || window.location.origin;
    return `${base}/view/${labelId}`;
  };

  // 处理预览
  const handlePreview = (labelId) => {
    const url = getLabelUrl(labelId);
    // 在新窗口打开预览
    window.open(url, '_blank');
  };

  // 处理编辑
  const handleEdit = async (labelId) => {
    if (!onEditLabel) {
      console.warn('onEditLabel回调未提供');
      return;
    }

    try {
      // 获取标签详情
      const response = await fetchLabelDetail(labelId);
      const labelData = response?.data || response;
      
      if (labelData) {
        // 调用父组件提供的编辑回调
        onEditLabel(labelData);
      } else {
        alert('获取标签详情失败，请稍后重试');
      }
    } catch (error) {
      console.error('获取标签详情失败:', error);
      alert('获取标签详情失败，请稍后重试');
    }
  };

  return (
    <div className="label-list-container">
      <div className="label-list-header">
        <h2 className="profile-title profile-title-plain">我的创建</h2>
        <div className="label-list-actions">
          <button 
            className="btn-refresh" 
            onClick={refresh}
            disabled={isLoading}
            title="刷新列表"
          >
            <Refresh theme="outline" size={16} />
            刷新
          </button>
          <button 
            className="btn-create-label" 
            onClick={onCreateLabel}
            title="创建新标签"
          >
            <FileText theme="outline" size={16} />
            创建标签
          </button>
        </div>
      </div>

      {error && (
        <div className="label-list-error">
          <p>{error}</p>
          <button className="btn-retry" onClick={refresh}>重试</button>
        </div>
      )}

      {isLoading && labels.length === 0 ? (
        <div className="label-list-loading">
          <p>正在加载标签列表...</p>
        </div>
      ) : labels.length === 0 ? (
        <div className="label-list-empty">
          <FileText theme="outline" size={48} fill="#94a3b8" />
          <p>您还没有创建任何标签</p>
          <button className="btn-create-label-primary" onClick={onCreateLabel}>
            立即创建
          </button>
        </div>
      ) : (
        <>
          <div className="label-list-content">
            <table className="label-list-table">
              <thead>
                <tr>
                  <th>标签名称</th>
                  <th>创建时间</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {labels.map((label) => {
                  const labelId = label.id || label.labelId;
                  const labelName = label.labelName || label.name || '未命名标签';
                  const createTime = label.createTime || label.createdAt || label.createTime;
                  const status = label.status || 'draft';
                  const statusText = status === 'published' ? '已发布' : '草稿';

                  return (
                    <tr key={labelId}>
                      <td className="label-name-cell">
                        <span className="label-name" title={labelName}>
                          {labelName}
                        </span>
                      </td>
                      <td className="label-time-cell">
                        {formatDate(createTime)}
                      </td>
                      <td className="label-status-cell">
                        <span className={`label-status label-status-${status}`}>
                          {statusText}
                        </span>
                      </td>
                      <td className="label-actions-cell">
                        <div className="label-actions">
                          <button
                            className="btn-action btn-preview"
                            onClick={() => handlePreview(labelId)}
                            title="预览标签"
                          >
                            <PreviewOpen theme="outline" size={16} />
                            预览
                          </button>
                          <button
                            className="btn-action btn-edit"
                            onClick={() => handleEdit(labelId)}
                            disabled={isLoading}
                            title="编辑标签"
                          >
                            <Edit theme="outline" size={16} />
                            编辑
                          </button>
                          <button
                            className="btn-action btn-view-qrcode"
                            onClick={() => handleShowQRCode(labelId, labelName)}
                            title="查看二维码"
                          >
                            <BarCode theme="outline" size={16} />
                            查看二维码
                          </button>
                          <button
                            className="btn-action btn-delete"
                            onClick={() => handleDeleteClick(labelId)}
                            disabled={isLoading}
                            title="删除标签"
                          >
                            <Delete theme="outline" size={16} />
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 分页 */}
          {pagination.totalPages > 1 && (
            <div className="label-list-pagination">
              <button
                className="btn-page"
                onClick={() => changePage(pagination.pageNum - 1)}
                disabled={pagination.pageNum <= 1 || isLoading}
              >
                上一页
              </button>
              <span className="pagination-info">
                第 {pagination.pageNum} / {pagination.totalPages} 页
                {pagination.total > 0 && ` (共 ${pagination.total} 条)`}
              </span>
              <button
                className="btn-page"
                onClick={() => changePage(pagination.pageNum + 1)}
                disabled={pagination.pageNum >= pagination.totalPages || isLoading}
              >
                下一页
              </button>
            </div>
          )}
        </>
      )}

      {/* 二维码弹窗 */}
      {showQRCodeModal && currentLabelId && (
        <div className="qrcode-modal-overlay" onClick={handleCloseQRCodeModal}>
          <div className="qrcode-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="qrcode-modal-close" onClick={handleCloseQRCodeModal}>
              <CloseSmall theme="outline" size={18} />
            </button>
            <h2 className="qrcode-modal-title">标签二维码</h2>
            <p className="qrcode-modal-subtitle">{currentLabelName}</p>
            
            <div className="qrcode-modal-body">
              <div className="qrcode-display">
                <img 
                  src={`/api/foodlabel/labels/${currentLabelId}/qrcode`} 
                  alt="标签二维码" 
                  width={BUSINESS_CONFIG.QRCODE.DISPLAY_SIZE} 
                  height={BUSINESS_CONFIG.QRCODE.DISPLAY_SIZE}
                  style={{ border: '1px solid #e8eaed', borderRadius: 8, background: '#fff' }}
                  onError={(e) => { 
                    // 备用方案：使用外部API生成二维码
                    const size = BUSINESS_CONFIG.QRCODE.DISPLAY_SIZE;
                    const ecc = BUSINESS_CONFIG.QRCODE.ERROR_CORRECTION_LEVEL;
                    const shareLink = getLabelUrl(currentLabelId);
                    e.currentTarget.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&ecc=${ecc}&data=${encodeURIComponent(shareLink)}`; 
                  }}
                />
              </div>
              <div className="qrcode-actions">
                <a
                  className="btn-qrcode-download"
                  href={`/api/foodlabel/labels/${currentLabelId}/qrcode`}
                  download={`label-${currentLabelId}-qrcode.png`}
                >
                  下载二维码
                </a>
              </div>
            </div>

            <div className="qrcode-modal-footer">
              <button className="btn-qrcode-close" onClick={handleCloseQRCodeModal}>完成</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
